import React, { useState, useEffect } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import "remixicon/fonts/remixicon.css";

// Shape of a single empty month/note
const emptyNote = () => ({
    reportWrittenBy: "",
    meetingDate: "",
    agenda: "",
    summary: "",
    actionPlan: "",
    familyMembers: [{ name: "" }],
    teamMembers: [{ name: "" }],
});

const FamilyMeetingMinutesForm = ({ initialData = {}, onChange }) => {
    const [clientName, setClientName] = useState("");
    const [notes, setNotes] = useState([emptyNote()]);

    // ✅ Sync initialData (Edit / prefill)
    useEffect(() => {
        if (initialData && Object.keys(initialData).length > 0) {
            setClientName(initialData.clientName || "");
            if (Array.isArray(initialData.notes) && initialData.notes.length > 0) {
                setNotes(
                    initialData.notes.map((n) => ({
                        reportWrittenBy: n.reportWrittenBy ?? n.report_written_by ?? "",
                        meetingDate: (n.meetingDate ?? n.meeting_date ?? "") ? String(n.meetingDate ?? n.meeting_date).split("T")[0] : "",
                        agenda: n.agenda ?? "",
                        summary: n.summary ?? "",
                        actionPlan: n.actionPlan ?? n.action_plan ?? "",
                        familyMembers: (n.familyMembers ?? n.family_members ?? []).length > 0
                            ? (n.familyMembers ?? n.family_members).map((m) => ({ name: m.name || "" }))
                            : [{ name: "" }],
                        teamMembers: (n.teamMembers ?? n.team_members ?? []).length > 0
                            ? (n.teamMembers ?? n.team_members).map((m) => ({ name: m.name || "" }))
                            : [{ name: "" }],
                    }))
                );
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initialData]);

    // ===== Note (month) handlers =====
    const addNote = () => setNotes((prev) => [...prev, emptyNote()]);
    const removeNote = (i) => setNotes((prev) => prev.filter((_, idx) => idx !== i));
    const setNoteField = (i, field, value) =>
        setNotes((prev) => prev.map((n, idx) => (idx === i ? { ...n, [field]: value } : n)));

    // ===== Member handlers (per note) =====
    const addMember = (i, key) =>
        setNotes((prev) => prev.map((n, idx) => (idx === i ? { ...n, [key]: [...n[key], { name: "" }] } : n)));
    const removeMember = (i, key, mIdx) =>
        setNotes((prev) => prev.map((n, idx) => (idx === i ? { ...n, [key]: n[key].filter((_, x) => x !== mIdx) } : n)));
    const setMember = (i, key, mIdx, value) =>
        setNotes((prev) =>
            prev.map((n, idx) =>
                idx === i ? { ...n, [key]: n[key].map((m, x) => (x === mIdx ? { name: value } : m)) } : n
            )
        );

    // Emit to parent
    useEffect(() => {
        if (onChange) onChange({ clientName, notes });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [clientName, notes]);

    return (
        <form onSubmit={(e) => e.preventDefault()} className="p-4">
            <div className="col-12 mb-4">
                <small className="text-dark bg-primary-subtle rounded-4 fw-bold d-block mb-3 px-3 py-1">
                    Family Meeting Minutes
                </small>
            </div>

            {/* Client Info (parent) */}
            <div className="row mb-4">
                <div className="col-md-6">
                    <div className="form-floating form-floating-outline">
                        <input
                            type="text"
                            className="form-control"
                            value={clientName}
                            onChange={(e) => setClientName(e.target.value)}
                            placeholder="Client Name"
                            required
                            readOnly={!!initialData?.clientName}
                        />
                        <label>Client Name</label>
                    </div>
                </div>
            </div>

            {/* Notes (Months) repeater */}
            {notes.map((note, i) => (
                <div key={`note-${i}`} className="border rounded-4 p-3 mb-4 shadow-sm bg-light-subtle">
                    <div className="d-flex justify-content-between align-items-center mb-3">
                        <small className="text-dark bg-primary-subtle rounded-4 fw-bold px-3 py-1">
                            Month {i + 1}
                        </small>
                        {notes.length > 1 && (
                            <div className="justify-content-end">
                                <button type="button" className="btn btn-outline-danger btn-sm" onClick={() => removeNote(i)}>
                                    <i className="ri-delete-bin-line"></i> Remove Month
                                </button>
                            </div>
                        )}
                    </div>

                    {/* Report written by + date */}
                    <div className="row mb-3 g-3">
                        <div className="col-md-6">
                            <div className="form-floating form-floating-outline">
                                <input
                                    type="text"
                                    className="form-control"
                                    value={note.reportWrittenBy}
                                    onChange={(e) => setNoteField(i, "reportWrittenBy", e.target.value)}
                                    placeholder="Report Written By"
                                />
                                <label>Report Written By</label>
                            </div>
                        </div>
                        <div className="col-md-6">
                            <div className="form-floating form-floating-outline">
                                <input
                                    type="date"
                                    className="form-control"
                                    value={note.meetingDate}
                                    onChange={(e) => setNoteField(i, "meetingDate", e.target.value)}
                                />
                                <label>Meeting Date</label>
                            </div>
                        </div>
                    </div>

                    {/* Family & Team Members */}
                    <div className="row g-3 mb-3">
                        {/* Family */}
                        <div className="col-md-6">
                            <small className="text-dark bg-secondary-subtle rounded-4 fw-bold d-block mb-2 px-3 py-1">
                                Family Members Present
                            </small>
                            {note.familyMembers.map((m, mIdx) => (
                                <div className="row align-items-center mb-2 g-2" key={`fam-${i}-${mIdx}`}>
                                    <div className="col-1 text-center text-muted">{mIdx + 1}.</div>
                                    <div className="col-9">
                                        <div className="form-floating form-floating-outline">
                                            <input
                                                type="text"
                                                className="form-control"
                                                value={m.name}
                                                onChange={(e) => setMember(i, "familyMembers", mIdx, e.target.value)}
                                                placeholder="Name"
                                            />
                                            <label>Name</label>
                                        </div>
                                    </div>
                                    <div className="col-2">
                                        {note.familyMembers.length > 1 && (
                                            <button type="button" className="btn btn-outline-danger w-100" onClick={() => removeMember(i, "familyMembers", mIdx)}>
                                                <i className="ri-close-line"></i>
                                            </button>
                                        )}
                                    </div>
                                </div>
                            ))}
                            <button type="button" className="btn btn-outline-primary btn-sm mt-1 w-100" onClick={() => addMember(i, "familyMembers")}>
                                <i className="ri-add-line"></i> Add Family Member
                            </button>
                        </div>

                        {/* Team */}
                        <div className="col-md-6">
                            <small className="text-dark bg-secondary-subtle rounded-4 fw-bold d-block mb-2 px-3 py-1">
                                Team Members Present
                            </small>
                            {note.teamMembers.map((m, mIdx) => (
                                <div className="row align-items-center mb-2 g-2" key={`team-${i}-${mIdx}`}>
                                    <div className="col-1 text-center text-muted">{mIdx + 1}.</div>
                                    <div className="col-9">
                                        <div className="form-floating form-floating-outline">
                                            <input
                                                type="text"
                                                className="form-control"
                                                value={m.name}
                                                onChange={(e) => setMember(i, "teamMembers", mIdx, e.target.value)}
                                                placeholder="Name"
                                            />
                                            <label>Name</label>
                                        </div>
                                    </div>
                                    <div className="col-2">
                                        {note.teamMembers.length > 1 && (
                                            <button type="button" className="btn btn-outline-danger w-100" onClick={() => removeMember(i, "teamMembers", mIdx)}>
                                                <i className="ri-close-line"></i>
                                            </button>
                                        )}
                                    </div>
                                </div>
                            ))}
                            <button type="button" className="btn btn-outline-primary btn-sm mt-1 w-100" onClick={() => addMember(i, "teamMembers")}>
                                <i className="ri-add-line"></i> Add Team Member
                            </button>
                        </div>
                    </div>

                    {/* Agenda / Summary / Action Plan */}
                    <div className="mb-3">
                        <small className="text-dark bg-secondary-subtle rounded-4 fw-bold d-block mb-2 px-3 py-1">Agenda of Meeting</small>
                        <textarea className="form-control" rows="2" value={note.agenda} onChange={(e) => setNoteField(i, "agenda", e.target.value)} placeholder="Enter the agenda of meeting..." />
                    </div>
                    <div className="mb-3">
                        <small className="text-dark bg-secondary-subtle rounded-4 fw-bold d-block mb-2 px-3 py-1">Summary of Discussion</small>
                        <textarea className="form-control" rows="2" value={note.summary} onChange={(e) => setNoteField(i, "summary", e.target.value)} placeholder="Enter summary of discussion..." />
                    </div>
                    <div className="mb-1">
                        <small className="text-dark bg-secondary-subtle rounded-4 fw-bold d-block mb-2 px-3 py-1">Action Plan Agreed Upon</small>
                        <textarea className="form-control" rows="2" value={note.actionPlan} onChange={(e) => setNoteField(i, "actionPlan", e.target.value)} placeholder="Enter the agreed action plan..." />
                    </div>
                </div>
            ))}

            {/* Add Month */}
            <div className="text-end">
                <button type="button" className="btn btn-outline-primary" onClick={addNote}>
                    <i className="ri-add-line"></i> Add Month
                </button>
            </div>
        </form>
    );
};

export default FamilyMeetingMinutesForm;
